import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { BookingService } from '@/services/BookingService';
import { ClientService } from '@/services/ClientService';
import { getJson } from '@/lib/http';
import { Booking } from '@shared/schema';

// Schema
const bookingSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    advertiserId: z.string().min(1, 'Account is required'), // Form uses strings for Select
    clientId: z.number(), // Derived, strictly typed as number in submitted data
    cycleId: z.string().min(1, 'Cycle is required'),
    retailerId: z.string().min(1, 'Retailer is required'),
    status: z.enum(['pipeline', 'optioned', 'booked', 'cancelled']),
    bookingValue: z.string().optional(), // Input as string, convert to number
    eventName: z.string().optional(),
    brand: z.string().optional(),
    description: z.string().optional(),
    commercialNotes: z.string().optional(),
    bookedByContactId: z.string().optional(),
    retailerEventId: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingSchema>;

interface BookingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    booking?: Booking | null; // If null, create mode
}

export function BookingDialog({ open, onOpenChange, booking }: BookingDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEditing = !!booking;

    // fetch data for dropdowns
    const { data: advertisers = [], isLoading: isLoadingAdvertisers } = useQuery<any[]>({
        queryKey: ['admin-advertisers'],
        queryFn: async () => {
            const response = await getJson('/api/admin/advertisers') as any;
            return response?.data?.advertisers || [];
        },
    });

    const { data: retailers = [], isLoading: isLoadingRetailers } = useQuery<any[]>({
        queryKey: ['retailers'],
        queryFn: async () => (await getJson('/api/retailers')) as any[],
    });

    const form = useForm<BookingFormValues>({
        resolver: zodResolver(bookingSchema),
        defaultValues: {
            name: '',
            advertiserId: '',
            clientId: 0,
            cycleId: '',
            retailerId: '',
            status: 'pipeline',
            bookingValue: '',
            eventName: '',
            brand: '',
            description: '',
            commercialNotes: '',
            bookedByContactId: '',
            retailerEventId: '',
        },
    });

    // Watch values
    const selectedRetailerId = form.watch('retailerId');
    const selectedAdvertiserId = form.watch('advertiserId');
    const clientIdValue = form.watch('clientId');
    const currentStatus = form.watch('status');
    const isNew = !isEditing;

    const { data: cycles = [], isLoading: isLoadingCycles } = useQuery<any[]>({
        queryKey: ['booking-cycles', selectedRetailerId],
        queryFn: async () => {
            const allCycles = await getJson('/api/booking-cycles') as any[];
            if (selectedRetailerId) {
                return allCycles.filter((c: any) => c.retailerId?.toString() === selectedRetailerId);
            }
            return allCycles;
        },
        enabled: !!selectedRetailerId,
    });

    // Retailer Event Logic
    const { data: retailerEvents = [], isLoading: isLoadingEvents } = useQuery<any[]>({
        queryKey: ['retailer-events', selectedRetailerId],
        queryFn: async () => {
            if (!selectedRetailerId) return [];
            const response = await getJson(`/api/retailers/${selectedRetailerId}/events`) as any;
            return response?.data || [];
        },
        enabled: !!selectedRetailerId,
    });

    // Fetch contacts for the selected advertiser's client (billing entity)
    const { data: contactsResponse, isLoading: isLoadingContacts } = useQuery({
        queryKey: ['client-contacts', clientIdValue],
        queryFn: () => ClientService.getContacts(clientIdValue),
        enabled: clientIdValue > 0,
    });
    const contacts = contactsResponse?.data || [];

    // Auto-select if only one contact exists (new booking only)
    useEffect(() => {
        if (!isEditing && contacts.length === 1 && !form.getValues('bookedByContactId')) {
            form.setValue('bookedByContactId', contacts[0].id.toString(), { shouldValidate: false });
        }
    }, [contacts, isEditing]);

    // Reset form when booking changes
    useEffect(() => {
        if (booking && open) {
            form.reset({
                name: booking.name,
                advertiserId: booking.advertiserId.toString(),
                clientId: booking.clientId,
                cycleId: booking.cycleId.toString(),
                retailerId: booking.retailerId.toString(),
                status: booking.status as any,
                bookingValue: booking.bookingValue?.toString() || '',
                eventName: booking.eventName || '',
                brand: booking.brand || '',
                description: booking.description || '',
                commercialNotes: booking.commercialNotes || '',
                bookedByContactId: booking.bookedByContactId?.toString() || '',
                retailerEventId: booking.retailerEventId?.toString() || '',
            });
        } else if (!booking && open) {
            form.reset({
                name: '',
                advertiserId: '',
                clientId: 0,
                cycleId: '',
                retailerId: '',
                status: 'pipeline',
                bookingValue: '',
                eventName: '',
                brand: '',
                description: '',
                commercialNotes: '',
                bookedByContactId: '',
                retailerEventId: '',
            });
        }
    }, [booking, open]);

    // Derived Client Logic
    const [derivedClientName, setDerivedClientName] = useState<string>('');

    useEffect(() => {
        if (selectedAdvertiserId && advertisers.length > 0) {
            const advertiser = advertisers.find((a: any) => a.id.toString() === selectedAdvertiserId);
            if (advertiser) {
                if (advertiser.clientId) {
                    form.setValue('clientId', advertiser.clientId, { shouldValidate: false });
                    setDerivedClientName(advertiser.clientName || 'Unknown Billing Entity');
                } else {
                    form.setValue('clientId', 0, { shouldValidate: false }); // Invalid ID
                    setDerivedClientName('⚠️ Account has no billing entity linked');
                }
            }
        } else if (!isEditing) {
            setDerivedClientName('');
            form.setValue('clientId', 0, { shouldValidate: false });
        }
    }, [selectedAdvertiserId, advertisers, isEditing]);

    // Status Guards
    const isStatusDisabled = (targetStatus: string) => {
        if (isNew) return targetStatus !== 'pipeline';

        if (booking?.status === 'cancelled') return true;
        if (booking?.status === targetStatus) return false;

        if (targetStatus === 'cancelled') return false;

        if (booking?.status === 'pipeline') {
            return targetStatus !== 'optioned';
        }
        if (booking?.status === 'optioned') {
            return targetStatus !== 'booked' && targetStatus !== 'pipeline';
        }
        if (booking?.status === 'booked') {
            return true;
        }

        return false;
    };

    const mutation = useMutation({
        mutationFn: (values: BookingFormValues) => {
            const payload: any = {
                ...values,
                advertiserId: parseInt(values.advertiserId),
                cycleId: parseInt(values.cycleId),
                retailerId: parseInt(values.retailerId),
                bookingValue: values.bookingValue ? parseFloat(values.bookingValue) : null,
                clientId: values.clientId,
                bookedByContactId: values.bookedByContactId ? parseInt(values.bookedByContactId) : null,
                retailerEventId: values.retailerEventId ? parseInt(values.retailerEventId) : null,
            };

            if (values.clientId === 0) {
                throw new Error("Selected account must have a linked billing entity to create a booking.");
            }

            if (isEditing && booking) {
                return BookingService.updateBooking(booking.id, payload);
            } else {
                return BookingService.createBooking(payload);
            }
        },
        onSuccess: () => {
            toast({
                title: isEditing ? 'Booking Updated' : 'Booking Created',
                description: `Successfully ${isEditing ? 'updated' : 'created'} booking.`,
            });
            onOpenChange(false);
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.message || 'Something went wrong',
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: BookingFormValues) => {
        mutation.mutate(values);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Edit Booking' : 'New Booking'}</DialogTitle>
                    <DialogDescription>
                        {isEditing ? 'Update booking details.' : 'Create a new commercial planning record.'}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        {/* Core Identity */}
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="advertiserId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Account</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Account" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {isLoadingAdvertisers ? (
                                                    <div className="p-2 text-sm text-muted-foreground">Loading...</div>
                                                ) : (
                                                    advertisers.map((a: any) => (
                                                        <SelectItem key={a.id} value={a.id.toString()}>
                                                            {a.name}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormItem>
                                <FormLabel>Billing Entity (Derived)</FormLabel>
                                <div className="relative">
                                    <Input
                                        value={derivedClientName}
                                        disabled
                                        className={derivedClientName.startsWith('⚠️') ? 'border-amber-500 bg-amber-50 text-amber-900' : 'bg-gray-50'}
                                    />
                                </div>
                                <FormDescription className="text-xs">
                                    Inherited from Account
                                </FormDescription>
                            </FormItem>

                            <FormField
                                control={form.control}
                                name="bookedByContactId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Booked by</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Contact" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {isLoadingContacts ? (
                                                    <div className="p-2 text-sm text-muted-foreground text-center">
                                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                                        Loading...
                                                    </div>
                                                ) : contacts.length === 0 ? (
                                                    <div className="p-2 text-sm text-muted-foreground text-center italic font-light">
                                                        No contacts found
                                                    </div>
                                                ) : (
                                                    contacts.map((c: any) => (
                                                        <SelectItem key={c.id} value={c.id.toString()}>
                                                            {c.name} {c.role ? `(${c.role})` : ''}
                                                        </SelectItem>
                                                    ))
                                                )}
                                            </SelectContent>
                                        </Select>
                                        {contacts.length === 0 && !isLoadingContacts && clientIdValue > 0 && (
                                            <FormDescription className="text-[10px] leading-tight italic text-amber-600">
                                                No contacts found for this billing entity. Add them in Account Management.
                                            </FormDescription>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="retailerId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Retailer</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Retailer" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {retailers.map((r: any) => (
                                                    <SelectItem key={r.id} value={r.id.toString()}>
                                                        {r.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="cycleId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Cycle</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select Cycle" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {cycles.map((c: any) => (
                                                    <SelectItem key={c.id} value={c.id.toString()}>
                                                        {c.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Details */}
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Booking Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="e.g. Q4 Cola Campaign" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="retailerEventId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Retailer Event (Optional)</FormLabel>
                                    <Select
                                        disabled={!selectedRetailerId || isLoadingEvents}
                                        onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                                        value={field.value || 'none'}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={!selectedRetailerId ? "Select a retailer first" : (isLoadingEvents ? "Loading events..." : "Select an event")} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="none">No Event</SelectItem>
                                            {retailerEvents.map((event: any) => (
                                                <SelectItem key={event.id} value={event.id.toString()}>
                                                    {event.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormDescription>
                                        Link this booking to a retailer-scoped planning event.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="bookingValue"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Value (Estimate)</FormLabel>
                                        <FormControl>
                                            <Input type="number" placeholder="0.00" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="status"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Status</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value} disabled={booking?.status === 'cancelled'}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Status" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="pipeline" disabled={isStatusDisabled('pipeline')}>Pipeline</SelectItem>
                                                <SelectItem value="optioned" disabled={isStatusDisabled('optioned')}>Optioned</SelectItem>
                                                <SelectItem value="booked" disabled={isStatusDisabled('booked')}>Booked</SelectItem>
                                                <SelectItem value="cancelled" className="text-red-600 focus:text-red-600">Cancelled</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            {booking?.status === 'cancelled' ? 'Terminal status.' : 'Pipeline → Optioned → Booked'}
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="commercialNotes"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Commercial Notes</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder="Internal notes..." {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {isEditing ? 'Save Changes' : 'Create Booking'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
